/* eslint-disable no-console */
import React, { createContext, useMemo, useState, FC, useEffect, useRef } from 'react';
import { useMutation, ExecutionResult } from 'react-apollo';
import { ExternalQuestion } from '@sm/question-widgets/respondent-survey';
import submitSurveyResponse from './SubmitResponses.graphql';
import {
  GetCurrentSurveySessionQuery,
  SubmitSurveyResponseMutation,
  SubmitSurveyResponseMutationVariables,
} from '~lib/generatedGqlTypes';
import { QuestionAnswers } from '~app/pages/SurveyTaking/types';
import { mapSurveyAnswersToResponse } from './helper/mapSurveyAnswers';

type Survey = GetCurrentSurveySessionQuery['surveyTakingSession']['respondent']['survey'];
type Collector = GetCurrentSurveySessionQuery['surveyTakingSession']['respondent']['collector'];
type ActiveSurveyPage = GetCurrentSurveySessionQuery['surveyTakingSession']['respondent']['currentPage'];

type SurveyContextType = {
  survey?: Survey;
  collector?: Collector;
  activePage?: ActiveSurveyPage;
  isMutationLoading: boolean;
  nextPage(this: void): void;
  previousPage(this: void): void;
  submitSurvey(
    this: void,
    answers: QuestionAnswers,
    mappedQuestions: ExternalQuestion[]
  ): Promise<ExecutionResult<SubmitSurveyResponseMutation>>;
  getSurveyStartTime(this: void): string;
};

const DEFAULT_PROMISE_RESOLVER = {
  data: {
    submitSurveyResponse: {
      encryptedSmParam: '',
      success: false,
      userErrors: null,
      respondent: null,
    },
  },
};

export const SurveyContext = createContext<SurveyContextType>({
  survey: undefined,
  collector: undefined,
  activePage: undefined,
  nextPage: () => {},
  previousPage: () => {},
  submitSurvey: async () => {
    return Promise.resolve(DEFAULT_PROMISE_RESOLVER);
  },
  getSurveyStartTime: () => '',
  isMutationLoading: false,
});

export type SurveyProviderProps = {
  children: React.ReactNode;
  survey: Survey;
  clientToken?: string;
  currentPage: GetCurrentSurveySessionQuery['surveyTakingSession']['respondent']['currentPage'];
  collector: GetCurrentSurveySessionQuery['surveyTakingSession']['respondent']['collector'];
};

const SurveyProvider: FC<SurveyProviderProps> = ({
  children,
  survey: initialSurvey,
  currentPage,
  collector,
  clientToken,
}) => {
  // Survey only rendering first page until we have information about who is responsible for providing next page.
  // When we get more information about how we will be acquiring other pages we will add those mechanisms here.
  const activePage = useMemo(() => currentPage, [currentPage]);
  // Survey currently holds error state, setSurvey will need to be called when upstream actions are hooked up.
  const [survey] = useState(initialSurvey);
  const startTime = useRef('');

  // Need to get client token here
  const [submitCurrentResponses, { loading: isMutationLoading }] = useMutation<
    SubmitSurveyResponseMutation,
    SubmitSurveyResponseMutationVariables
  >(submitSurveyResponse, {
    context: {
      headers: {
        'x-client-token': clientToken ?? null,
      },
    },
  });

  // Set StartTime on Load
  useEffect(() => {
    startTime.current = new Date().toISOString();
  }, []);

  const getSurveyStartTime = (): string => {
    return startTime.current;
  };

  const nextPage = (): void => {
    console.log('Use this method to get the next page.  Pending decision about pagination');
  };
  const previousPage = (): void => {
    console.log('Use this method to get the previous page.  Pending decision about pagination');
  };

  const submitSurvey = async (
    answers: QuestionAnswers,
    mappedQuestions: ExternalQuestion[]
  ): Promise<ExecutionResult<SubmitSurveyResponseMutation>> => {
    if (survey) {
      try {
        // Only use the answered questions in payload (SPAGE-7410)
        const responses = mapSurveyAnswersToResponse({
          answers,
          currentPageId: currentPage?.id ?? '',
          mappedQuestions,
        });

        const submitResponsesPromise = submitCurrentResponses({
          variables: {
            surveyId: survey?.id ?? '',
            input: {
              collectorId: collector?.id ?? '',
              isAnonymous: collector?.isAnonymous ?? false,
              surveyVersion: survey?.version ?? 1,
              dateStarted: getSurveyStartTime(),
              dateCompleted: new Date().toISOString(),
              respondentId: '',
              responses,
            },
          },
        });

        const submitResponses = await submitResponsesPromise;

        return submitResponses;
      } catch (error: unknown) {
        // To be completed in server error validation story
        console.log(error);
      }
    }
    return Promise.resolve(DEFAULT_PROMISE_RESOLVER);
  };

  return (
    <SurveyContext.Provider
      value={{
        survey,
        collector,
        activePage,
        nextPage,
        previousPage,
        submitSurvey,
        getSurveyStartTime,
        isMutationLoading,
      }}
    >
      <>{children}</>
    </SurveyContext.Provider>
  );
};

export default SurveyProvider;
